var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Constants } from "@babylonjs/core/Engines/constants";
import { MaterialDefines } from "@babylonjs/core/Materials/materialDefines";
import { MaterialPluginBase } from "@babylonjs/core/Materials/materialPluginBase";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { serialize, serializeAsTexture } from "@babylonjs/core/Misc/decorators";
import { RegisterClass } from "@babylonjs/core/Misc/typeStore";
import { MmdBufferKind } from "./mmdBufferKind";
/**
 * for convert MMD material to Babylon material
 *
 * use StandardMaterial as base class
 *
 * propertiy mapping:
 *
 * - diffuse[0..2]: diffuseColor
 * - specular: specularColor
 * - ambient: ambientColor
 * - diffuse[3](opaque): alpha
 * - shininess(reflect): specularPower
 * - isDoubleSided: backFaceCulling
 * - enabledToonEdge: (custom implementation)
 * - edgeColor: (custom implementation)
 * - edgeSize: (custom implementation)
 * - texture: diffuseTexture
 * - sphereTexture: (custom implementation)
 * - toonTexture: (custom implementation)
 *
 * using options:
 *
 * useAlphaFromDiffuseTexture
 *
 * additinal implementation:
 *
 * spherical deformation
 */
// https://cyos.babylonjs.com/
// spherical environment mapping reference:
// https://learn.microsoft.com/en-us/windows/win32/direct3d9/spherical-environment-mapping
export class MmdPluginMaterialDefines extends MaterialDefines {
    SPHERE_TEXTURE = false;
    SPHERE_TEXTURE_BLEND_MODE_MULTIPLY = false;
    SPHERE_TEXTURE_BLEND_MODE_ADD = false;
    TOON_TEXTURE = false;
    IGNORE_DIFFUSE_WHEN_TOON_TEXTURE_DISABLED = false;
    APPLY_AMBIENT_COLOR_TO_DIFFUSE = false;
    CLAMP_ALPHA = false;
    TEXTURE_COLOR = false;
    SPHERE_TEXTURE_COLOR = false;
    TOON_TEXTURE_COLOR = false;
    SDEF = false;
}
export var MmdPluginMaterialSphereTextureBlendMode;
(function (MmdPluginMaterialSphereTextureBlendMode) {
    MmdPluginMaterialSphereTextureBlendMode[MmdPluginMaterialSphereTextureBlendMode["Multiply"] = 1] = "Multiply";
    MmdPluginMaterialSphereTextureBlendMode[MmdPluginMaterialSphereTextureBlendMode["Add"] = 2] = "Add";
    MmdPluginMaterialSphereTextureBlendMode[MmdPluginMaterialSphereTextureBlendMode["SubTexture"] = 3] = "SubTexture";
})(MmdPluginMaterialSphereTextureBlendMode || (MmdPluginMaterialSphereTextureBlendMode = {}));
export class MmdPluginMaterial extends MaterialPluginBase {
    _sphereTexture = null;
    _sphereTextureBlendMode = MmdPluginMaterialSphereTextureBlendMode.Add;
    _toonTexture = null;
    _ignoreDiffuseWhenToonTextureIsNull = false;
    textureMultiplicativeColor = new Color4(1, 1, 1, 1);
    textureAdditiveColor = new Color4(0, 0, 0, 0);
    sphereTextureMultiplicativeColor = new Color4(1, 1, 1, 1);
    sphereTextureAdditiveColor = new Color4(0, 0, 0, 0);
    toonTextureMultiplicativeColor = new Color4(1, 1, 1, 1);
    toonTextureAdditiveColor = new Color4(0, 0, 0, 0);
    _applyAmbientColorToDiffuse = true;
    _clampAlpha = true;
    _useTextureColor = false;
    _useSphereTextureColor = false;
    _useToonTextureColor = false;
    _isEnabled = false;
    get isEnabled() {
        return this._isEnabled;
    }
    set isEnabled(value) {
        if (this._isEnabled === value)
            return;
        this._isEnabled = value;
        this.markAllDefinesAsDirty();
        this._enable(value);
    }
    get sphereTexture() {
        return this._sphereTexture;
    }
    set sphereTexture(value) {
        if (this._sphereTexture === value)
            return;
        this._sphereTexture = value;
        this._markAllSubMeshesAsTexturesDirty();
    }
    get sphereTextureBlendMode() {
        return this._sphereTextureBlendMode;
    }
    set sphereTextureBlendMode(value) {
        if (this._sphereTextureBlendMode === value)
            return;
        this._sphereTextureBlendMode = value;
        this.markAllDefinesAsDirty();
    }
    get toonTexture() {
        return this._toonTexture;
    }
    set toonTexture(value) {
        if (this._toonTexture === value)
            return;
        this._toonTexture = value;
        this._markAllSubMeshesAsTexturesDirty();
    }
    get ignoreDiffuseWhenToonTextureIsNull() {
        return this._ignoreDiffuseWhenToonTextureIsNull;
    }
    set ignoreDiffuseWhenToonTextureIsNull(value) {
        if (this._ignoreDiffuseWhenToonTextureIsNull === value)
            return;
        this._ignoreDiffuseWhenToonTextureIsNull = value;
        this.markAllDefinesAsDirty();
    }
    get applyAmbientColorToDiffuse() {
        return this._applyAmbientColorToDiffuse;
    }
    set applyAmbientColorToDiffuse(value) {
        if (this._applyAmbientColorToDiffuse === value)
            return;
        this._applyAmbientColorToDiffuse = value;
        this.markAllDefinesAsDirty();
    }
    get clampAlpha() {
        return this._clampAlpha;
    }
    set clampAlpha(value) {
        if (this._clampAlpha === value)
            return;
        this._clampAlpha = value;
        this.markAllDefinesAsDirty();
    }
    get useTextureColor() {
        return this._useTextureColor;
    }
    set useTextureColor(value) {
        if (this._useTextureColor === value)
            return;
        this._useTextureColor = value;
        this.markAllDefinesAsDirty();
    }
    get useSphereTextureColor() {
        return this._useSphereTextureColor;
    }
    set useSphereTextureColor(value) {
        if (this._useSphereTextureColor === value)
            return;
        this._useSphereTextureColor = value;
        this.markAllDefinesAsDirty();
    }
    get useToonTextureColor() {
        return this._useToonTextureColor;
    }
    set useToonTextureColor(value) {
        if (this._useToonTextureColor === value)
            return;
        this._useToonTextureColor = value;
        this.markAllDefinesAsDirty();
    }
    _internalMarkAllSubMeshesAsTexturesDirty;
    _markAllSubMeshesAsTexturesDirty() {
        this._enable(this._isEnabled);
        this._internalMarkAllSubMeshesAsTexturesDirty();
    }
    /**
     * Gets a boolean indicating that the plugin is compatible with a given shader language.
     * @param shaderLanguage The shader language to use.
     * @returns true if the plugin is compatible with the shader language
     */
    isCompatible(_shaderLanguage) {
        return true;
    }
    constructor(material, addtoPluginList = true, enable = false, resolveIncludes = false) {
        super(material, "MmdMaterial", 100, new MmdPluginMaterialDefines(), addtoPluginList, enable, resolveIncludes);
        this._internalMarkAllSubMeshesAsTexturesDirty = material._dirtyCallbacks[Constants.MATERIAL_TextureDirtyFlag];
    }
    isReadyForSubMesh(defines, scene) {
        if (!this._isEnabled)
            return true;
        if (defines._areTexturesDirty && scene.texturesEnabled) {
            if (this._sphereTexture && !this._sphereTexture.isReadyOrNotBlocking()) {
                return false;
            }
        }
        return true;
    }
    bindForSubMesh(uniformBuffer, scene, _engine, subMesh) {
        if (!this._isEnabled)
            return;
        const defines = subMesh.materialDefines;
        const isFrozen = this._material.isFrozen;
        if (!uniformBuffer.useUbo || !isFrozen || !uniformBuffer.isSync) {
            if (defines.DIFFUSE && defines.TEXTURE_COLOR) {
                uniformBuffer.updateDirectColor4("textureMultiplicativeColor", this.textureMultiplicativeColor);
                uniformBuffer.updateDirectColor4("textureAdditiveColor", this.textureAdditiveColor);
            }
            if (defines.NORMAL && defines.SPHERE_TEXTURE && defines.SPHERE_TEXTURE_COLOR) {
                uniformBuffer.updateDirectColor4("sphereTextureMultiplicativeColor", this.sphereTextureMultiplicativeColor);
                uniformBuffer.updateDirectColor4("sphereTextureAdditiveColor", this.sphereTextureAdditiveColor);
            }
            if (defines.TOON_TEXTURE && defines.TOON_TEXTURE_COLOR) {
                uniformBuffer.updateDirectColor4("toonTextureMultiplicativeColor", this.toonTextureMultiplicativeColor);
                uniformBuffer.updateDirectColor4("toonTextureAdditiveColor", this.toonTextureAdditiveColor);
            }
            if (defines.SPHERE_TEXTURE && subMesh.effect !== null) {
                this._material.bindView(subMesh.effect);
            }
        }
        if (scene.texturesEnabled) {
            if (defines.NORMAL && this._sphereTexture)
                uniformBuffer.setTexture("sphereSampler", this._sphereTexture);
            if (this._toonTexture)
                uniformBuffer.setTexture("toonSampler", this._toonTexture);
        }
    }
    dispose(forceDisposeTextures) {
        if (forceDisposeTextures) {
            this._sphereTexture?.dispose();
            this._toonTexture?.dispose();
        }
    }
    prepareDefines(defines, scene, mesh) {
        if (this._isEnabled) {
            const texturesEnabled = scene.texturesEnabled;
            defines.SPHERE_TEXTURE = this._sphereTexture !== null && texturesEnabled;
            defines.SPHERE_TEXTURE_BLEND_MODE_MULTIPLY = this._sphereTextureBlendMode === MmdPluginMaterialSphereTextureBlendMode.Multiply;
            defines.SPHERE_TEXTURE_BLEND_MODE_ADD = this._sphereTextureBlendMode === MmdPluginMaterialSphereTextureBlendMode.Add;
            // todo: support sub texture mode
            defines.TOON_TEXTURE = this._toonTexture !== null && texturesEnabled;
            defines.IGNORE_DIFFUSE_WHEN_TOON_TEXTURE_DISABLED = this._ignoreDiffuseWhenToonTextureIsNull;
            defines.APPLY_AMBIENT_COLOR_TO_DIFFUSE = this._applyAmbientColorToDiffuse;
            defines.CLAMP_ALPHA = this._clampAlpha;
            defines.TEXTURE_COLOR = this._useTextureColor;
            defines.SPHERE_TEXTURE_COLOR = this._useSphereTextureColor;
            defines.TOON_TEXTURE_COLOR = this._useToonTextureColor;
            defines.SDEF = mesh.useBones && mesh.computeBonesUsingShaders && (mesh.skeleton ? true : false) && mesh.isVerticesDataPresent(MmdBufferKind.MatricesSdefCKind);
        }
        else {
            defines.SPHERE_TEXTURE = false;
            defines.SPHERE_TEXTURE_BLEND_MODE_MULTIPLY = false;
            defines.SPHERE_TEXTURE_BLEND_MODE_ADD = false;
            defines.TOON_TEXTURE = false;
            defines.IGNORE_DIFFUSE_WHEN_TOON_TEXTURE_DISABLED = false;
            defines.APPLY_AMBIENT_COLOR_TO_DIFFUSE = false;
            defines.CLAMP_ALPHA = false;
            defines.TEXTURE_COLOR = false;
            defines.SPHERE_TEXTURE_COLOR = false;
            defines.TOON_TEXTURE_COLOR = false;
            defines.SDEF = false;
        }
    }
    hasTexture(texture) {
        return this._sphereTexture === texture || this._toonTexture === texture;
    }
    getActiveTextures(activeTextures) {
        if (this._sphereTexture)
            activeTextures.push(this._sphereTexture);
        if (this._toonTexture)
            activeTextures.push(this._toonTexture);
    }
    getAnimatables(animatables) {
        if (this._sphereTexture && this._sphereTexture.animations && 0 < this._sphereTexture.animations.length) {
            animatables.push(this._sphereTexture);
        }
        if (this._toonTexture && this._toonTexture.animations && 0 < this._toonTexture.animations.length) {
            animatables.push(this._toonTexture);
        }
    }
    getSamplers(samplers) {
        samplers.push("sphereSampler", "toonSampler");
    }
    getAttributes(attributes, _scene, mesh) {
        if (this._isEnabled) {
            if (mesh.useBones && mesh.computeBonesUsingShaders && mesh.skeleton && mesh.isVerticesDataPresent(MmdBufferKind.MatricesSdefCKind)) {
                attributes.push(MmdBufferKind.MatricesSdefCKind);
                attributes.push(MmdBufferKind.MatricesSdefRW0Kind);
                attributes.push(MmdBufferKind.MatricesSdefRW1Kind);
            }
        }
    }
    getUniforms(_shaderLanguage) {
        return {
            "ubo": [
                { name: "textureMultiplicativeColor", size: 4, type: "vec4" },
                { name: "textureAdditiveColor", size: 4, type: "vec4" },
                { name: "sphereTextureMultiplicativeColor", size: 4, type: "vec4" },
                { name: "sphereTextureAdditiveColor", size: 4, type: "vec4" },
                { name: "toonTextureMultiplicativeColor", size: 4, type: "vec4" },
                { name: "toonTextureAdditiveColor", size: 4, type: "vec4" }
            ]
        };
    }
    getClassName() {
        return "MmdPluginMaterial";
    }
}
__decorate([
    serializeAsTexture("sphereTexture")
], MmdPluginMaterial.prototype, "_sphereTexture", void 0);
__decorate([
    serialize("sphereTextureBlendMode")
], MmdPluginMaterial.prototype, "_sphereTextureBlendMode", void 0);
__decorate([
    serializeAsTexture("toonTexture")
], MmdPluginMaterial.prototype, "_toonTexture", void 0);
__decorate([
    serialize("ignoreDiffuseWhenToonTextureIsNull")
], MmdPluginMaterial.prototype, "_ignoreDiffuseWhenToonTextureIsNull", void 0);
__decorate([
    serialize("applyAmbientColorToDiffuse")
], MmdPluginMaterial.prototype, "_applyAmbientColorToDiffuse", void 0);
__decorate([
    serialize("clampAlpha")
], MmdPluginMaterial.prototype, "_clampAlpha", void 0);
RegisterClass("BABYLON.MmdPluginMaterial", MmdPluginMaterial);
